import {
  Badge,
  Box,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useMapa } from "../../../contexts/mapaContext";
import { convertBitsToBytesUniversal } from "../../../utils/easyMaps/convertToBytes";
import DraggableModal from "../draggableModal";

const formatItemValue = (item) =>
  isNaN(item?.lastvalue)
    ? { valor: item?.lastvalue ?? "--", unidade: "" }
    : convertBitsToBytesUniversal(
        parseFloat(item.lastvalue).toFixed(2),
        item.units,
      );

const ellipsis = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
};

// abre à esquerda, ao lado da toolbar, para não cobrir o painel de Enlaces
const defaultBoxLeft = () => ({
  w: 360,
  h: window.innerHeight - 140,
  x: 80,
  y: 80,
});

const ItensPanel = ({ open, onClose }) => {
  const theme = useTheme();
  const { hostsDataMap, elements, lastCollectData } = useMapa();
  const loading = !lastCollectData;

  const [query, setQuery] = useState("");

  // hosts com itens selecionados na config do elemento ("Selecione os itens do host")
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [];
    hostsDataMap?.forEach((hosts, elementId) => {
      const elemento = elements?.get(elementId)?.titulo ?? "";
      hosts?.forEach((host) => {
        const itens = host?.itensData ?? [];
        if (itens.length === 0) return;
        const texto = `${host.name} ${elemento} ${itens.map((i) => i.name).join(" ")}`;
        if (q && !texto.toLowerCase().includes(q)) return;
        list.push({
          id: `${elementId}:${host.hostid}`,
          host: host.name,
          elemento,
          isDown: host.isDown,
          itens,
        });
      });
    });
    return list.sort((a, b) => a.host.localeCompare(b.host));
  }, [hostsDataMap, elements, query]);

  return (
    <DraggableModal
      open={open}
      onClose={onClose}
      title="Itens dos hosts"
      storageKey="easyMapsItensPanel"
      defaultBox={defaultBoxLeft}
    >
      <Stack gap={1.25} sx={{ px: 1.5, pb: 1.25 }}>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar host ou item"
          inputProps={{ "aria-label": "Buscar host ou item" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
            sx: { fontSize: "0.82rem" },
          }}
        />
      </Stack>

      <Box
        component="ol"
        aria-label="Hosts e valores dos itens"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          flex: 1,
          overflowY: "auto",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {loading &&
          [0, 1, 2].map((i) => (
            <Stack
              key={i}
              component="li"
              aria-hidden="true"
              gap={0.5}
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Skeleton variant="text" width="45%" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </Stack>
          ))}

        {!loading &&
          rows.map((r) => (
            <Box
              key={r.id}
              component="li"
              sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  px: 1.5,
                  pt: 1.25,
                  pb: 0.5,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: theme.palette.background.paperSelectedMaps,
                }}
              >
                <Badge variant="dot" color={r.isDown ? "error" : "success"} />
                <Box
                  component="span"
                  title={r.host}
                  sx={{
                    flex: 1,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    ...ellipsis,
                  }}
                >
                  {r.host}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                >
                  {r.itens.length} {r.itens.length === 1 ? "item" : "itens"}
                </Box>
              </Stack>

              <Box component="dl" sx={{ m: 0, pl: 2.3, pr: 1.5, pb: 1.25 }}>
                {r.itens.map((item, idx) => {
                  const { valor, unidade } = formatItemValue(item);
                  return (
                    <Stack
                      key={`${item.key_}-${idx}`}
                      direction="row"
                      alignItems="baseline"
                      gap={2}
                      sx={{ py: 0.4 }}
                    >
                      <Box
                        component="dt"
                        title={item.name}
                        sx={{
                          flex: 1,
                          fontSize: "0.78rem",
                          color: theme.palette.text.secondary,
                          ...ellipsis,
                        }}
                      >
                        {item.name}
                      </Box>
                      <Box
                        component="dd"
                        sx={{
                          m: 0,
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                          color: r.isDown
                            ? theme.palette.text.disabled
                            : theme.palette.text.primary,
                        }}
                      >
                        {valor}
                        {unidade && (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              fontSize: "0.68rem",
                              fontWeight: 500,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {unidade}
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  );
                })}
              </Box>
            </Box>
          ))}

        {!loading && rows.length === 0 && (
          <Box
            component="li"
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              fontSize: "0.82rem",
              color: theme.palette.text.secondary,
            }}
          >
            {query
              ? `Nenhum host ou item encontrado para “${query}”.`
              : "Nenhum host deste mapa tem itens selecionados."}
          </Box>
        )}
      </Box>
    </DraggableModal>
  );
};

export default ItensPanel;
